export type DecodedImageResult =
  | { kind: "data-url"; dataUrl: string }
  | { kind: "blob-url"; blobUrl: string; revoke: () => void }

import { HEIC_MIME_TYPES, TIFF_MIME_TYPES } from "@lib/constants/mime"

export type DecodeImageInput = {
  file: File
  /** Optional: when provided, helps infer type when browser leaves file.type empty. */
  fallbackMimeType?: string
}

export function inferMimeTypeFromFilename(
  filename?: string,
): string | undefined {
  if (!filename) return undefined
  const lower = filename.toLowerCase()
  if (lower.endsWith(".heic")) return "image/heic"
  if (lower.endsWith(".heif")) return "image/heif"
  if (lower.endsWith(".tiff") || lower.endsWith(".tif")) return "image/tiff"
  return undefined
}

function isHeic(mime: string): boolean {
  return HEIC_MIME_TYPES.has(mime)
}

function isTiff(mime: string): boolean {
  return TIFF_MIME_TYPES.has(mime)
}

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error || new Error("FileReader error"))
    reader.onload = () => {
      if (!reader.result) return reject(new Error("Failed to read file"))
      resolve(String(reader.result))
    }
    reader.readAsDataURL(file)
  })
}

/**
 * Decodes HEIC/HEIF + TIFF to something the browser can render in an <img>.
 * For normal browser-supported images (jpeg/png/webp/etc) it returns a data-url.
 */
export async function decodeImageToDisplayableSrc({
  file,
  fallbackMimeType,
}: DecodeImageInput): Promise<DecodedImageResult> {
  const mime =
    file.type ||
    inferMimeTypeFromFilename(file.name) ||
    fallbackMimeType

  if (!mime) {
    throw new Error(
      `Unable to determine mime type for file: ${file.name || "unknown"}`,
    )
  }

  // If it's not HEIC or TIFF, assume the browser can handle it natively (JPEG, PNG, WebP, etc.)
  // and return a data URL.
  // (This is also the simplest path for unit tests.)
  if (!isHeic(mime) && !isTiff(mime)) {
    return { kind: "data-url", dataUrl: await fileToDataUrl(file) }
  }

  if (isHeic(mime)) {
    // Lazy-load heic2any so we don't bloat the main bundle.
    const { default: heic2any } = await import("heic2any")
    const converted = (await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.92,
    })) as Blob | Blob[]

    const blob = Array.isArray(converted) ? converted[0] : converted
    const blobUrl = URL.createObjectURL(blob)
    return {
      kind: "blob-url",
      blobUrl,
      revoke: () => URL.revokeObjectURL(blobUrl),
    }
  }

  // TIFF
  const utif = await import("utif")
  const buffer = await file.arrayBuffer()
  const ifds = utif.decode(buffer)
  if (!ifds || ifds.length === 0) {
    throw new Error("Invalid TIFF: no IFDs")
  }
  const first = ifds[0]
  utif.decodeImage(buffer, first, ifds)

  const rgba = utif.toRGBA8(first)
  const width = first.width
  const height = first.height
  if (!width || !height) throw new Error("Invalid TIFF: missing dimensions")

  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Canvas 2d context not available")

  const imageData = ctx.createImageData(width, height)
  imageData.data.set(rgba)
  ctx.putImageData(imageData, 0, 0)

  const dataUrl = canvas.toDataURL("image/png")
  return { kind: "data-url", dataUrl }
}
